import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, RefreshCw, Unplug, Link2, AlertTriangle, CheckCircle2, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function SheetsPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => api.get('/admin/sheets/status').then(r => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const flag = searchParams.get('sheets');
    if (flag === 'connected') toast.success("Google Sheets connecté : vos commandes sont synchronisées");
    if (flag === 'error') toast.error(`Connexion Google impossible : ${searchParams.get('reason') || 'erreur'}`);
    if (flag) { searchParams.delete('sheets'); searchParams.delete('reason'); setSearchParams(searchParams, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try { const r = await api.get('/admin/sheets/auth-url'); window.location.href = r.data.url; }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try { const r = await api.post('/admin/sheets/sync'); toast.success(`${r.data.rows} commandes synchronisées`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur de synchronisation"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Déconnecter Google Sheets ? La feuille existante reste dans votre Drive.")) return;
    await api.delete('/admin/sheets'); toast.success("Google Sheets déconnecté"); load();
  };

  if (!status) return null;
  const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="sheets-panel">
      <div className="flex items-center gap-2 mb-1">
        <div className="h-8 w-8 rounded-lg bg-emerald-600 flex items-center justify-center"><Table2 className="h-4 w-4 text-white" /></div>
        <h2 className="text-base md:text-lg font-bold text-slate-900">Google Sheets</h2>
        {status.connected
          ? <Badge className="text-xs border bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="sheets-status-badge">Connecté</Badge>
          : <Badge className="text-xs border bg-slate-100 text-slate-600 border-slate-200" data-testid="sheets-status-badge">{status.configured ? 'Non connecté' : 'À configurer'}</Badge>}
      </div>

      {!status.configured && (
        <div className="mt-3 text-sm text-slate-600 space-y-2" data-testid="sheets-setup-help">
          <p>Pour copier automatiquement vos commandes dans une feuille Google, il faut un accès Google Cloud (gratuit, 5 min) :</p>
          <ol className="list-decimal pl-5 space-y-1 text-slate-500">
            <li>Sur <span className="font-medium text-slate-700">console.cloud.google.com</span>, créez un projet et activez « Google Sheets API ».</li>
            <li>Écran de consentement OAuth : type Externe, ajoutez votre adresse Google en utilisateur test.</li>
            <li>Identifiants → ID client OAuth → Application Web, avec l'URI de redirection ci-dessous.</li>
            <li>Transmettez le Client ID et le Client Secret à votre assistant Emergent : ils seront stockés dans la configuration secrète.</li>
          </ol>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs font-mono break-all" data-testid="sheets-redirect-uri">{status.redirect_uri}</div>
          <p className="text-xs text-slate-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> En attendant, l'export Excel ci-dessus et le tableau en direct couvrent le suivi.</p>
        </div>
      )}

      {status.configured && !status.connected && (
        <div className="mt-3">
          <p className="text-sm text-slate-600">Connectez votre compte Google : une feuille « Plume et Pion - Commandes » sera créée dans votre Drive et mise à jour à chaque commande.</p>
          <Button onClick={connect} disabled={busy} className="mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="sheets-connect-btn"><Link2 className="h-4 w-4 mr-2" /> Connecter Google Sheets</Button>
        </div>
      )}

      {status.connected && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-slate-600">Compte : <span className="font-medium text-slate-900">{status.email || '—'}</span></p>
          <p className="text-sm text-slate-600 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Dernière synchro : {fmt(status.last_sync)}</p>
          {status.last_error && <p className="text-xs text-rose-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {status.last_error}</p>}
          <div className="flex flex-wrap gap-2">
            {status.spreadsheet_url && (
              <a href={status.spreadsheet_url} target="_blank" rel="noreferrer" data-testid="sheets-open-link">
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Ouvrir la feuille</Button>
              </a>
            )}
            <Button size="sm" variant="outline" className="rounded-full" onClick={sync} disabled={busy} data-testid="sheets-sync-btn"><RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? 'animate-spin' : ''}`} /> Synchroniser</Button>
            <Button size="sm" variant="ghost" className="rounded-full text-slate-500 hover:text-rose-600" onClick={disconnect} data-testid="sheets-disconnect-btn"><Unplug className="h-3.5 w-3.5 mr-1.5" /> Déconnecter</Button>
          </div>
        </div>
      )}
    </div>
  );
}
